import React, { useMemo, useState } from 'react';
import { Plus } from "../../icons";
import { Autocomplete } from "../Autocomplete";
import { Typography } from "../Typography";
import { TextButton } from "../TextButton";
import { getOptionLabel, getOptionSelected } from "../Autocomplete/AutocompleteSelectors";
import { pathgen } from 'object-path-generator';
import { sortBy } from 'lodash-es';
import { jsx as _jsx } from "react/jsx-runtime";
export let EnhancedAutocompleteOptionType = /*#__PURE__*/function (EnhancedAutocompleteOptionType) {
  EnhancedAutocompleteOptionType["Item"] = "item";
  EnhancedAutocompleteOptionType["NewItem"] = "new-item";
  EnhancedAutocompleteOptionType["EmptySearch"] = "empty-search";
  EnhancedAutocompleteOptionType["IncompleteSearch"] = "incomplete-search";
  return EnhancedAutocompleteOptionType;
}({});
const mapDataToOption = (data, keyField, labelField) => ({
  value: data[keyField],
  label: data[labelField],
  data,
  type: EnhancedAutocompleteOptionType.Item
});
const getOptions = (options, keyField, labelField) => {
  const mappedOptions = options.map(item => mapDataToOption(item, keyField, labelField));
  return sortBy(mappedOptions, option => option.label.toLowerCase());
};
export const EnhancedAutocompleteTestIds = pathgen('EnhancedAutocompleteTestIds');
export const EnhancedAutocomplete = props => {
  var _value$label;
  const {
    ['data-testid']: testId,
    applyOptionChange: _applyOptionChange,
    options: _options,
    menuWidth,
    emptySearchText,
    addNewItemText,
    onAddItemClick,
    isLoading,
    onRefresh,
    textFieldProps,
    className,
    style
  } = props;
  const [currentOption, setCurrentOption] = useState(null);
  const options = useMemo(() => {
    return getOptions(_options, props.keyField, props.labelField);
  }, [_options, props.keyField, props.labelField]);
  const value = currentOption;
  const valueLabel = (_value$label = value === null || value === void 0 ? void 0 : value.label) !== null && _value$label !== void 0 ? _value$label : '';
  const applyOptionChange = newOption => {
    setCurrentOption(newOption);
    const item = newOption === null || newOption === void 0 ? void 0 : newOption.data;
    _applyOptionChange === null || _applyOptionChange === void 0 || _applyOptionChange(item);
  };
  return /*#__PURE__*/_jsx(Autocomplete, {
    selectOnFocus: true,
    className: className,
    style: style,
    size: "small",
    menuWidth: menuWidth,
    textFieldProps: textFieldProps,
    forcePopupIcon: true,
    "data-testid": testId !== null && testId !== void 0 ? testId : EnhancedAutocompleteTestIds.EnhancedAutocomplete(),
    loading: isLoading,
    options: options,
    renderOption: option => {
      if ((option === null || option === void 0 ? void 0 : option.type) === EnhancedAutocompleteOptionType.NewItem) {
        return /*#__PURE__*/_jsx(TextButton, {
          "data-testid": EnhancedAutocompleteTestIds.NewItemOption(),
          underline: "none",
          prefixIcon: /*#__PURE__*/_jsx(Plus, {}),
          children: addNewItemText
        });
      }
      if ((option === null || option === void 0 ? void 0 : option.type) === EnhancedAutocompleteOptionType.EmptySearch) {
        return /*#__PURE__*/_jsx(Typography, {
          as: "p",
          "data-testid": EnhancedAutocompleteTestIds.EmptySearchOption(),
          children: option.label
        });
      }
      return /*#__PURE__*/_jsx(Typography, {
        as: "p",
        ellipsis: true,
        children: option.label
      });
    },
    filterOptions: options => {
      const NO_MATCHES_OPTION = {
        value: 'empty-search',
        label: emptySearchText,
        type: EnhancedAutocompleteOptionType.EmptySearch
      };
      const NEW_OPTION = {
        value: 'new-service',
        label: addNewItemText,
        type: EnhancedAutocompleteOptionType.NewItem
      };
      const filteredOptions = options.filter(option => option.label.toLowerCase().includes(valueLabel.toLowerCase()));
      return [NEW_OPTION, ...(filteredOptions.length === 0 ? [NO_MATCHES_OPTION] : []), ...filteredOptions];
    },
    freeSolo: true,
    value: value,
    inputValue: valueLabel,
    getOptionLabel: getOptionLabel,
    getOptionSelected: getOptionSelected,
    onBlur: () => {
      if ((currentOption === null || currentOption === void 0 ? void 0 : currentOption.type) !== EnhancedAutocompleteOptionType.Item) {
        applyOptionChange(null);
      }
    },
    onInputChange: (_, value, reason) => {
      if (reason === 'clear') {
        applyOptionChange(null);
        return;
      }
      if (reason !== 'input') return; // Other reasons are picked up by onChange handler.
      const option = options.find(s => s.label === value);
      const fallbackServiceOptionValue = {
        value: '',
        label: value,
        type: EnhancedAutocompleteOptionType.IncompleteSearch
      };
      applyOptionChange(option !== null && option !== void 0 ? option : fallbackServiceOptionValue);
    },
    getOptionDisabled: option => option.type === EnhancedAutocompleteOptionType.EmptySearch,
    onChange: async (event, value) => {
      const newValue = value ? value : null;
      if ((newValue === null || newValue === void 0 ? void 0 : newValue.type) === EnhancedAutocompleteOptionType.NewItem) {
        const newItem = await onAddItemClick();
        await (onRefresh === null || onRefresh === void 0 ? void 0 : onRefresh());
        if (newItem) {
          applyOptionChange(mapDataToOption(newItem, props.keyField, props.labelField));
        }
      } else {
        applyOptionChange(newValue);
      }
    }
  });
};